import React, { useState } from "react";
import rp from "../assets/retirement-planning.webp";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { toast } from "react-toastify";

const ContactUs = () => {
  const [serviceFormData, setServiceFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: [],
    query: "", // <-- Added query field
  });

  const [serviceErrors, setServiceErrors] = useState({});

  const handleServiceChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "services") {
      setServiceFormData((prevData) => ({
        ...prevData,
        services: checked
          ? [...prevData.services, value]
          : prevData.services.filter((service) => service !== value),
      }));
    } else {
      setServiceFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const validateServiceForm = () => {
    const newErrors = {};

    if (!serviceFormData.name || serviceFormData.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters.";
    }

    if (
      !serviceFormData.email ||
      !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(serviceFormData.email)
    ) {
      newErrors.email = "Invalid email format.";
    }

    if (
      !serviceFormData.phone ||
      serviceFormData.phone.length !== 10 ||
      !/^\d{10}$/.test(serviceFormData.phone)
    ) {
      newErrors.phone = "Phone number must be 10 digits.";
    }

    if (!serviceFormData.services || serviceFormData.services.length === 0) {
      newErrors.services = "At least one service must be selected.";
    }

    if (!serviceFormData.query || serviceFormData.query.trim().length < 5) {
      newErrors.query = "Query must be at least 5 characters.";
    }

    if (Object.keys(newErrors).length > 0) {
      Object.values(newErrors).forEach((msg) => toast.error(msg));
    }

    setServiceErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();

    if (!validateServiceForm()) return;

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_SERVICE_TEMPLATE,
        {
          user_name: serviceFormData.name,
          user_email: serviceFormData.email,
          user_phone: serviceFormData.phone,
          user_services: serviceFormData.services.join(", "),
          user_query: serviceFormData.query, // <-- Added query for email
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      toast.success(
        "Query sent successfully. Our team will get back to you as soon as possible"
      );
      setServiceFormData({
        name: "",
        email: "",
        phone: "",
        services: [],
        query: "",
      });
    } catch (error) {
      console.error("Email sending error:", error);
      toast.error("Failed to send inquiry. Please try again later.");
    }
  };

  return (
    <>
      <div className="bg-white">
        <div className="flex flex-col items-center justify-center">
          <div className="bg-primary w-full flex justify-center items-center">
            <h1 className="text-2xl font-bold text-white my-8">Contact Us</h1>
          </div>
          <p className="text-center text-black leading-relaxed mb-4 px-16">
            Thank you for visiting our website! We value your interest and look
            forward to connecting with you.
          </p>
          <p className="text-center text-black leading-relaxed mb-4 px-16">
            Whether you have questions, feedback, or are interested in working
            with us, we'd love to hear from you.
          </p>
          <p className="text-center text-black leading-relaxed mb-4 px-16">
            Please don't hesitate to reach out to us through the form.
          </p>
          <p className="text-center text-black leading-relaxed mb-4 px-16">
            We appreciate your time and look forward to connecting with you
            soon!
          </p>
        </div>
      </div>

      <div className="bg-gray-100 p-6 flex justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg mx-auto bg-black bg-opacity-90 p-8 rounded-lg shadow-lg"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${rp})`,
            backgroundSize: "contain",
          }}
        >
          <h2 className="text-3xl font-bold mb-6 text-center text-white">
            Service Inquiry Form
          </h2>
          <form onSubmit={handleServiceSubmit} className="space-y-4">
            <input
              type="text"
              name="name"
              value={serviceFormData.name}
              onChange={handleServiceChange}
              placeholder="Name"
              className="w-full p-3 text-white placeholder:text-white rounded border border-gray-300 bg-white bg-opacity-20"
              required
            />
            <input
              type="email"
              name="email"
              value={serviceFormData.email}
              onChange={handleServiceChange}
              placeholder="Email"
              className="w-full p-3 text-white placeholder:text-white rounded border border-gray-300 bg-white bg-opacity-20"
              required
            />
            <input
              type="tel"
              name="phone"
              value={serviceFormData.phone}
              onChange={handleServiceChange}
              placeholder="Phone"
              className="w-full p-3 text-white placeholder:text-white rounded border border-gray-300 bg-white bg-opacity-20"
              required
            />

            <textarea
              name="query"
              value={serviceFormData.query}
              onChange={handleServiceChange}
              placeholder="Your Query"
              rows="4"
              className="w-full p-3 text-white placeholder:text-white rounded border border-gray-300 bg-white bg-opacity-20"
              required
            />

            <div className="space-y-2">
              <p className="text-white">Services you're interested in:</p>
              {[
                "Financial Planning",
                "Wealth Management",
                "Mutual Funds",
                "Insurance",
                "Children Education",
              ].map((service) => (
                <label key={service} className="flex items-center text-white">
                  <input
                    type="checkbox"
                    name="services"
                    value={service}
                    checked={serviceFormData.services.includes(service)}
                    onChange={handleServiceChange}
                    className="mr-2"
                  />
                  {service}
                </label>
              ))}
            </div>

            {serviceErrors.services && (
              <p className="text-white text-lg text-center font-semibold">
                {serviceErrors.services}
              </p>
            )}

            {serviceErrors.query && (
              <p className="text-white text-lg text-center font-semibold">
                {serviceErrors.query}
              </p>
            )}

            <div className="flex justify-center">
              <button
                type="submit"
                className="bg-primary hover:bg-[#1e1b5a] text-white px-6 py-3 rounded-lg transition"
              >
                Submit Inquiry
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>
  );
};

export default ContactUs;
